import React, { useState, useEffect } from 'react';
import { auth, googleProvider } from '../firebase';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { getUserRole } from '../utils/firebaseAuth';
import './AdminGate.css';
import TechLogin from './TechLogin';
import { getPreviewRole } from '../utils/previewRole';

const AdminGate = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleChecked, setRoleChecked] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (authUser) => {
      setUser(authUser);
      setLoading(false);
      setRoleChecked(!authUser);
    });
    return unsubscribe;
  }, []);

  // Dev preview helper: /?forceLogin=1 signs out so the login page is visible
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('forceLogin') === '1' && auth.currentUser) {
        signOut(auth).finally(() => {
          params.delete('forceLogin');
          const qs = params.toString();
          window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
        });
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!user?.email) {
      setRoleChecked(true);
      setIsAuthorized(false);
      return;
    }
    setRoleChecked(false);
    getUserRole(user.email).then((role) => {
      if (!cancelled) {
        setRoleChecked(true);
        setIsAuthorized(role === 'admin' || role === 'consultant' || role === 'support');
      }
    });
    return () => { cancelled = true; };
  }, [user?.email]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  // Dev-only: ?previewRole=admin|consultant|support skips sign-in for UI preview
  const previewRole = getPreviewRole();
  if (previewRole && children) {
    return children;
  }

  if (loading || (user && !roleChecked)) {
    return (
      <div className="admin-gate-container">
        <div className="admin-gate-content">
          <div className="loading-spinner"></div>
          <p className="loading-text">Checking admin access...</p>
        </div>
      </div>
    );
  }

  // Not signed in
  if (!user) {
    return <TechLogin />;
  }

  // Signed in, role checked, but not authorized
  if (user && roleChecked && !isAuthorized) {
    return (
      <div className="admin-gate-container">
        <div className="admin-gate-content">
          <div className="admin-gate-header">
            <h1>Access Denied</h1>
            <p>You are not authorized to access this admin area.</p>
            <p className="user-email">Signed in as: {user.email}</p>
          </div>
          
          <div className="admin-gate-actions">
            <button 
              className="admin-signout-btn"
              onClick={handleSignOut}
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Authorized admin - render children
  return children;
};

export default AdminGate;
